import { Chip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { formatTranslation } from 'intl';
import { ReactElement } from 'react';
import { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import type { UserOnView } from 'shared/domain/user/types/view';
import { isUserOnViewWithIssueAccessLevel } from 'shared/domain/user/types/view';
import { getAccessLevelIcon } from 'views/issue/issueView/AccessManagement/getIcon';
import { UserCard } from '../card';
import { HoverWrapper } from '../card/hoverWrapper';
import { useIntl } from 'react-intl';
import { CommonTranslationKeys } from 'intl/keys';

export interface UserChipProps {
  user: UserOnView;
  userAccesses: UserAccess[];
  issueId: string;
  color: 'primary' | 'default';
  siteId: string;
  processId: string;
  /** Sits on the same line as surrounding text in an event description. */
  inlineWithText?: boolean;
}

const userChipSx = {
  fontSize: '1rem',
  verticalAlign: 'initial',
  p: '4px',
} satisfies SxProps<Theme>;

const inlineMentionUserChipSx = {
  height: 'auto',
  fontSize: 'inherit',
  alignItems: 'baseline',
  verticalAlign: 'baseline',
  '& .MuiChip-label': { px: '0.4em' },
  '& .MuiChip-icon': { alignSelf: 'center', fontSize: '1em' },
} satisfies SxProps<Theme>;

export function UserChip({
  user,
  userAccesses,
  issueId,
  color,
  siteId,
  processId,
  inlineWithText = false,
}: UserChipProps): ReactElement {
  const icon = isUserOnViewWithIssueAccessLevel(user)
    ? getAccessLevelIcon(user.accessLevel.value, false)
    : undefined;

  const onHoverComponent = (
    <UserCard
      user={user}
      userAccesses={userAccesses}
      issueId={issueId}
      siteId={siteId}
      processId={processId}
    />
  );
  return (
    <HoverWrapper onHoverComponent={onHoverComponent}>
      <Chip
        icon={icon}
        size='small'
        key={user._id}
        label={user.label || user.email}
        sx={inlineWithText ? inlineMentionUserChipSx : userChipSx}
        color={color}
        data-qa={`user-chip-${user._id}`}
      />
    </HoverWrapper>
  );
}

export function UserChipDeleted(): ReactElement {
  const intl = useIntl();
  const label = formatTranslation(intl, CommonTranslationKeys.userDeletedLabel,);

  return <Chip label={label} size='small' color='default' />;
}
